import { RETENTION_ENTITY_LABELS } from '@kortex/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Tabs } from '../components/Layout';
import { Empty, ErrorState, Field, Kpi, Loading, Modal, Select, TextInput, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime } from '../lib/format';
import { GuestRecordsTab, RequestsTab } from './privacy/RightsTabs';
import { HotelSupportAccessTab } from './privacy/SupportAccessTabs';
import { HotelProcessorsTab } from './privacy/ProcessorsTab';
import { ErasureLogTab } from './privacy/ErasureLogTab';
import { AccountExitPanel } from '../components/account/AccountExit';

/**
 * Privacy & Compliance, for a hotel: what it has recorded about consent, the
 * notice it shows guests, the documents its owner has accepted, and how each
 * purpose is classified.
 *
 * Later phases add data principal requests, retention and support access to
 * the same page - see docs/privacy/DPDP-IMPLEMENTATION-REPORT.md.
 */

type Tab = 'overview' | 'requests' | 'guests' | 'consent' | 'notices' | 'acceptances' | 'purposes' | 'retention' | 'support' | 'processors' | 'erasures' | 'audit' | 'account';

interface Overview {
  consents: Record<string, number>;
  openRequests: number;
  guestNotice: { id: string; version: string; scope: 'PLATFORM' | 'PROPERTY' } | null;
  purposesUndecided: number;
}

interface ConsentRow {
  id: string;
  subjectType: string;
  subjectId: string;
  purpose: string;
  status: 'GRANTED' | 'WITHDRAWN' | 'DECLINED';
  source: string;
  grantedAt: string | null;
  withdrawnAt: string | null;
  declinedAt: string | null;
  updatedAt: string;
  notice: { version: string; title: string } | null;
}

interface Notice {
  id: string;
  type: string;
  typeLabel: string;
  scope: 'PLATFORM' | 'PROPERTY';
  version: string;
  title: string;
  summary: string;
  content: string;
  status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
  hasPlaceholders: boolean;
  publishedAt: string | null;
  retiredAt: string | null;
  createdAt: string;
}

interface Purpose {
  purpose: string;
  label: string;
  description: string;
  processingBasis: string;
  consentRequired: boolean | null;
  decided: boolean;
}

const SUBJECT_LABELS: Record<string, string> = {
  GUEST: 'Room guest',
  BOOKING_GUEST: 'Online booking',
  RESERVATION_GUEST: 'Table reservation',
  ORDER_CUSTOMER: 'Online order',
  EMPLOYEE: 'Employee',
  USER: 'Staff account',
};

const STATUS_TONE: Record<string, string> = {
  GRANTED: 'ok',
  WITHDRAWN: 'warn',
  DECLINED: '',
  DRAFT: 'warn',
  PUBLISHED: 'ok',
  RETIRED: '',
};

export function PrivacyPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'requests', label: 'Data requests' },
    { id: 'guests', label: 'Guest records' },
    { id: 'erasures', label: 'Erasure log' },
    { id: 'consent', label: 'Consent' },
    { id: 'notices', label: 'Guest notice' },
    { id: 'acceptances', label: 'Accepted terms' },
    { id: 'purposes', label: 'Purposes' },
    { id: 'retention', label: 'Retention' },
    { id: 'support', label: 'SARTHI support access' },
    { id: 'processors', label: 'Processors' },
    ...(can('audit:view') ? [{ id: 'audit' as const, label: 'Audit' }] : []),
    ...(can('property:manage') ? [{ id: 'account' as const, label: 'Export & close account' }] : []),
  ];
  return (
    <div className="stack">
      <PageHeader
        title="Privacy & Compliance"
        subtitle="Consent, notices and the records you keep about how guests' data is handled"
      />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'overview' ? <OverviewTab onOpen={setTab} /> : null}
      {tab === 'requests' ? <RequestsTab /> : null}
      {tab === 'guests' ? <GuestRecordsTab /> : null}
      {tab === 'erasures' ? <ErasureLogTab /> : null}
      {tab === 'consent' ? <ConsentTab /> : null}
      {tab === 'notices' ? <NoticesTab /> : null}
      {tab === 'acceptances' ? <AcceptancesTab /> : null}
      {tab === 'purposes' ? <PurposesTab /> : null}
      {tab === 'retention' ? <RetentionTab /> : null}
      {tab === 'support' ? <HotelSupportAccessTab /> : null}
      {tab === 'processors' ? <HotelProcessorsTab /> : null}
      {tab === 'audit' ? (
        <div className="card">
          <div className="card-body col gap-14">
            <p className="small">
              Every privileged action at this property is in the Audit Log - sign-ins and refused attempts, exports,
              views of guest and payroll data, privacy actions, SARTHI support access. Filter it by kind there.
            </p>
            <div>
              <Link className="btn primary" to="/audit">
                Open the Audit Log
              </Link>
            </div>
          </div>
        </div>
      ) : null}
      {tab === 'account' ? <AccountExitPanel /> : null}
    </div>
  );
}

function OverviewTab({ onOpen }: { onOpen: (tab: Tab) => void }) {
  const overview = useQuery({
    queryKey: ['privacy-overview'],
    queryFn: () => api.get<Overview>('/api/privacy/overview'),
  });
  if (overview.isLoading) return <Loading />;
  if (overview.error) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const data = overview.data!;
  return (
    <>
      <div className="kpis">
        <Kpi label="Consents granted" value={data.consents.GRANTED ?? 0} />
        <Kpi label="Withdrawn" value={data.consents.WITHDRAWN ?? 0} tone="amber" />
        <Kpi label="Open data requests" value={data.openRequests} tone={data.openRequests > 0 ? 'amber' : ''} />
        <Kpi
          label="Purposes awaiting a legal decision"
          value={data.purposesUndecided}
          tone={data.purposesUndecided > 0 ? 'amber' : ''}
        />
      </div>
      <div className="card">
        <div className="card-head">
          <h3>Guest privacy notice</h3>
        </div>
        <div className="card-body small">
          {data.guestNotice ? (
            data.guestNotice.scope === 'PROPERTY' ? (
              <>Guests see your own notice, version {data.guestNotice.version}.</>
            ) : (
              <>
                Guests see SARTHI's default notice (version {data.guestNotice.version}). You can
                publish your own under{' '}
                <button type="button" className="linklike" onClick={() => onOpen('notices')}>
                  Guest notice
                </button>
                .
              </>
            )
          ) : (
            <>
              No guest notice is published yet, so guests are shown none. Publish one under{' '}
              <button type="button" className="linklike" onClick={() => onOpen('notices')}>
                Guest notice
              </button>
              .
            </>
          )}
        </div>
      </div>
    </>
  );
}

function ConsentTab() {
  const { can } = useAuth();
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [purpose, setPurpose] = useState('');
  const [status, setStatus] = useState('');
  const [history, setHistory] = useState<ConsentRow | null>(null);

  const consents = useQuery({
    queryKey: ['privacy-consents', purpose, status],
    queryFn: () =>
      api.get<ConsentRow[]>(
        `/api/privacy/consents?${new URLSearchParams({
          ...(purpose ? { purpose } : {}),
          ...(status ? { status } : {}),
        }).toString()}`,
      ),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => api.post(`/api/privacy/consents/${id}/withdraw`, { source: 'ADMIN' }),
    onSuccess: () => {
      push('Consent withdrawn - the record and its history are kept', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-consents'] });
      void queryClient.invalidateQueries({ queryKey: ['privacy-overview'] });
    },
    onError: fail,
  });

  return (
    <div className="card">
      <div className="card-head row between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h3>Consent register</h3>
        <div className="row gap-6">
          <Select label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            <option value="">All</option>
            {['MARKETING', 'SERVICE_DELIVERY', 'BOOKING', 'CHECK_IN', 'CUSTOMER_SUPPORT'].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="GRANTED">Granted</option>
            <option value="WITHDRAWN">Withdrawn</option>
            <option value="DECLINED">Declined</option>
          </Select>
        </div>
      </div>
      {consents.isLoading ? (
        <Loading />
      ) : consents.error ? (
        <ErrorState error={consents.error} onRetry={() => void consents.refetch()} />
      ) : (consents.data ?? []).length === 0 ? (
        <Empty message="No consent has been recorded. Consent is only asked for purposes a legal review has marked as needing it." />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Who</th>
                <th>Purpose</th>
                <th>Status</th>
                <th>Source</th>
                <th>Notice</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {consents.data!.map((row) => (
                <tr key={row.id}>
                  <td>
                    {SUBJECT_LABELS[row.subjectType] ?? row.subjectType}
                    <span className="xs muted mono"> · {row.subjectId.slice(0, 8)}</span>
                  </td>
                  <td>{row.purpose}</td>
                  <td>
                    <span className={`chip ${STATUS_TONE[row.status] ?? ''}`}>{row.status}</span>
                  </td>
                  <td>{row.source}</td>
                  <td>{row.notice ? `v${row.notice.version}` : '—'}</td>
                  <td>{dateTime(row.updatedAt)}</td>
                  <td>
                    <div className="row gap-6">
                      <button className="btn sm" onClick={() => setHistory(row)}>
                        History
                      </button>
                      {row.status === 'GRANTED' && can('privacy:manage') ? (
                        <button
                          className="btn sm"
                          disabled={withdraw.isPending}
                          onClick={() => withdraw.mutate(row.id)}
                        >
                          Withdraw
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {history ? <ConsentHistory consent={history} onClose={() => setHistory(null)} /> : null}
    </div>
  );
}

function ConsentHistory({ consent, onClose }: { consent: ConsentRow; onClose: () => void }) {
  const history = useQuery({
    queryKey: ['privacy-consent-history', consent.id],
    queryFn: () =>
      api.get<{ events: Array<{ id: string; status: string; source: string; at: string }> }>(
        `/api/privacy/consents/${consent.id}/history`,
      ),
  });
  return (
    <Modal title={`${consent.purpose} - history`} size="narrow" onClose={onClose}>
      {history.isLoading ? (
        <Loading />
      ) : (
        <ol className="small" style={{ paddingLeft: 18 }}>
          {(history.data?.events ?? []).map((event) => (
            <li key={event.id}>
              <b>{event.status}</b> via {event.source} · {dateTime(event.at)}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

function NoticesTab() {
  const { can } = useAuth();
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Notice | 'new' | null>(null);

  const notices = useQuery({
    queryKey: ['privacy-notices'],
    queryFn: () => api.get<Notice[]>('/api/privacy/notices'),
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'publish' | 'retire' }) =>
      api.post(`/api/privacy/notices/${id}/${action}`),
    onSuccess: (_data, { action }) => {
      push(action === 'publish' ? 'Published - guests see this version now' : 'Retired', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-notices'] });
      void queryClient.invalidateQueries({ queryKey: ['privacy-overview'] });
    },
    onError: fail,
  });

  const rows = (notices.data ?? []).filter((notice) => notice.type === 'GUEST_PRIVACY_NOTICE');
  const own = rows.filter((notice) => notice.scope === 'PROPERTY');
  const platformDefault = rows.find((notice) => notice.scope === 'PLATFORM' && notice.status === 'PUBLISHED');

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head row between">
          <h3>Your guest privacy notice</h3>
          {can('privacy:manage') ? (
            <button className="btn primary sm" onClick={() => setEditing('new')}>
              New version
            </button>
          ) : null}
        </div>
        <div className="card-body small muted">
          Shown wherever guests give their details - check-in, online booking, the QR menu and
          table reservations. A published version is never edited: draft a new one, and publishing
          it retires the old. {platformDefault ? `Until you publish your own, guests see SARTHI's default (v${platformDefault.version}).` : ''}
        </div>
        {notices.isLoading ? (
          <Loading />
        ) : own.length === 0 ? (
          <Empty message="You have not written your own notice yet." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {own.map((notice) => (
                  <tr key={notice.id}>
                    <td className="mono">{notice.version}</td>
                    <td>
                      {notice.title}
                      {notice.hasPlaceholders ? (
                        <span className="chip warn" style={{ marginLeft: 6 }}>
                          needs input
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`chip ${STATUS_TONE[notice.status] ?? ''}`}>{notice.status}</span>
                    </td>
                    <td>{notice.publishedAt ? date(notice.publishedAt) : '—'}</td>
                    <td>
                      {can('privacy:manage') ? (
                        <div className="row gap-6">
                          {notice.status === 'DRAFT' ? (
                            <>
                              <button className="btn sm" onClick={() => setEditing(notice)}>
                                Edit
                              </button>
                              <button
                                className="btn sm primary"
                                disabled={act.isPending || notice.hasPlaceholders}
                                title={notice.hasPlaceholders ? 'Replace the marked text first' : undefined}
                                onClick={() => act.mutate({ id: notice.id, action: 'publish' })}
                              >
                                Publish
                              </button>
                            </>
                          ) : null}
                          {notice.status === 'PUBLISHED' ? (
                            <button
                              className="btn sm"
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: notice.id, action: 'retire' })}
                            >
                              Retire
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing ? (
        <NoticeEditor
          notice={editing === 'new' ? null : editing}
          template={platformDefault ?? null}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function NoticeEditor({
  notice,
  template,
  onClose,
}: {
  notice: Notice | null;
  template: Notice | null;
  onClose: () => void;
}) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(notice?.version ?? '');
  const [title, setTitle] = useState(notice?.title ?? template?.title ?? 'Your privacy at this property');
  const [summary, setSummary] = useState(notice?.summary ?? template?.summary ?? '');
  const [content, setContent] = useState(notice?.content ?? template?.content ?? '');

  const save = useMutation({
    mutationFn: () =>
      notice
        ? api.patch(`/api/privacy/notices/${notice.id}`, { title, summary, content })
        : api.post('/api/privacy/notices', {
            type: 'GUEST_PRIVACY_NOTICE',
            version,
            title,
            summary,
            content,
          }),
    onSuccess: () => {
      push('Draft saved', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-notices'] });
      onClose();
    },
    onError: fail,
  });

  return (
    <Modal
      title={notice ? `Edit draft ${notice.version}` : 'New guest notice'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={save.isPending || !title.trim() || !summary.trim() || !content.trim() || (!notice && !version.trim())}
            onClick={() => save.mutate()}
          >
            Save draft
          </button>
        </>
      }
    >
      <div className="col gap-14">
        {!notice ? (
          <TextInput
            label="Version"
            placeholder="e.g. 2026-10-06 or 1.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
          />
        ) : null}
        <TextInput label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Field label="Summary - shown on the form">
          <textarea className="input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <Field label="Full notice">
          <textarea className="input" rows={14} value={content} onChange={(e) => setContent(e.target.value)} />
        </Field>
        <p className="xs muted">
          Text marked [BUSINESS INPUT REQUIRED] or [LEGAL REVIEW REQUIRED] has to be replaced before
          the notice can be published.
        </p>
      </div>
    </Modal>
  );
}

function AcceptancesTab() {
  const rows = useQuery({
    queryKey: ['privacy-acceptances'],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          notice: { type: string; version: string; title: string };
          acceptedBy: string | null;
          source: string;
          acceptedAt: string;
        }>
      >('/api/privacy/acceptances'),
  });
  if (rows.isLoading) return <Loading />;
  if (rows.error) return <ErrorState error={rows.error} onRetry={() => void rows.refetch()} />;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Documents accepted for this hotel</h3>
      </div>
      {(rows.data ?? []).length === 0 ? (
        <Empty message="No acceptance is on record. Hotels registered before acceptance history existed have none here." />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Document</th>
                <th>Version</th>
                <th>Accepted by</th>
                <th>How</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.data!.map((row) => (
                <tr key={row.id}>
                  <td>{row.notice.title}</td>
                  <td className="mono">{row.notice.version}</td>
                  <td>{row.acceptedBy ?? '—'}</td>
                  <td>{row.source === 'REGISTRATION' ? 'At registration' : 'Re-accepted'}</td>
                  <td>{dateTime(row.acceptedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PurposesTab() {
  const purposes = useQuery({
    queryKey: ['privacy-purposes'],
    queryFn: () => api.get<Purpose[]>('/api/privacy/purposes'),
  });
  if (purposes.isLoading) return <Loading />;
  if (purposes.error) return <ErrorState error={purposes.error} onRetry={() => void purposes.refetch()} />;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Why guests' data is processed</h3>
      </div>
      <div className="card-body small muted">
        The legal basis for each purpose, and whether guests must be asked to consent, are set by
        SARTHI once a legal review has decided them. Until then a purpose shows as awaiting a
        decision, and no consent question is asked for it.
      </div>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Purpose</th>
              <th>What it covers</th>
              <th>Basis</th>
              <th>Consent</th>
            </tr>
          </thead>
          <tbody>
            {purposes.data!.map((row) => (
              <tr key={row.purpose}>
                <td className="b">{row.label}</td>
                <td className="small">{row.description}</td>
                <td>{row.processingBasis === 'UNDECIDED' ? <span className="chip warn">Awaiting decision</span> : row.processingBasis}</td>
                <td>
                  {row.consentRequired === null ? '—' : row.consentRequired ? 'Asked' : 'Not asked'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface RetentionRow {
  entity: string;
  retentionDays: number;
  action: 'DELETE' | 'ANONYMIZE' | 'PURGE_SENSITIVE_FIELDS';
  enabled: boolean;
  source: 'platform' | 'account';
  decisionRequired: boolean;
  legalBasis?: string | null;
}

const RETENTION_ACTION: Record<RetentionRow['action'], string> = {
  DELETE: 'deleted',
  ANONYMIZE: 'personal data erased',
  PURGE_SENSITIVE_FIELDS: 'sensitive fields removed',
};

/** How long this account's records are kept. Read-only: SARTHI sets these. */
function RetentionTab() {
  const rows = useQuery({
    queryKey: ['privacy-retention'],
    queryFn: () => api.get<RetentionRow[]>('/api/privacy/retention'),
  });
  if (rows.isLoading) return <Loading />;
  if (rows.error) return <ErrorState error={rows.error} onRetry={() => void rows.refetch()} />;
  return (
    <div className="card">
      <div className="card-head">
        <h3>How long records are kept</h3>
      </div>
      <div className="card-body small muted">
        Set by SARTHI, not from here: a retention period can carry legal weight. Guest, booking, order and
        staff records are kept until a period has been decided - those show as kept. Issued invoices are
        never anonymised.
      </div>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Records</th>
              <th>Kept for</th>
              <th>Set for</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {rows.data!.map((row) => (
              <tr key={row.entity}>
                <td className="b">{RETENTION_ENTITY_LABELS[row.entity] ?? row.entity}</td>
                <td>
                  {row.enabled ? (
                    `${row.retentionDays} days, then ${RETENTION_ACTION[row.action]}`
                  ) : row.decisionRequired ? (
                    <span className="chip warn">Kept - period awaiting a decision</span>
                  ) : (
                    'Kept'
                  )}
                </td>
                <td>{row.source === 'account' ? 'This account' : 'All accounts'}</td>
                <td className="xs muted">{row.legalBasis ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
